import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/debounceTime';

import { XmlFileEditorService } from '../service/xml-file-editor.service';

type NodeLevel = 'root' | 'fund' | 'worker' | 'salary' | 'allocation';

// TreeNode used by this component. `data` is always a direct reference into the
// original raw JSON object for that item (never a copy, never extended with extra
// fields) so that marking "@uDeleted" mutates the real object we send back on Save.
interface XmlTreeNode extends TreeNode {
  levelType: NodeLevel;
  matched?: boolean;
  deleted?: boolean;
  label2?: string;
}

const SEARCHABLE_WORKER_FIELDS = ['SHEM-PRATI', 'SHEM-MISHPACHA', 'MISPAR-MEZAHE'];

@Component({
  selector: 'app-xml-file-editor',
  templateUrl: './xml-file-editor.component.html',
  styleUrls: ['./xml-file-editor.component.css']
})
export class XmlFileEditorComponent implements OnInit, OnDestroy {

  // Which file to load/save - wire this up to whatever identifies the file in your app.
  // @Input() fileId: string;
  @Input() jsonData: string;
  @Input() selectedCustomerId: string;
  @Input() msvEditFileName: string;
  @Output() onSaveMsvFinished = new EventEmitter<any>();

  treeNodes: XmlTreeNode[] = [];
  cols = [
    { field: 'name', header: 'רמה', width: 400 },
    { field: 'col1', header: 'פרטים 1' },
    { field: 'col2', header: 'פרטים 2' },
    { field: 'col3', header: 'פרטים 3' },
    { field: 'actions', header: 'סימון למיחקה', width: 110 }
  ];

  searchTerm = '';
  hideUnmatched = false;
  matchCount = 0;
  loading = false;
  saving = false;

  // The exact object received from the server. Never restructured - only individual
  // "@uDeleted" flags are mutated on the objects nested inside it.
  private rawData: any = null;

  private searchSubject = new Subject<void>();
  private searchSub: Subscription;

  constructor(private xmlFileEditorService: XmlFileEditorService) { }

  ngOnInit(): void {
    this.searchSub = this.searchSubject.debounceTime(250).subscribe(() => this.refreshTree());
    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
  }

  loadData(): void {
    this.loading = true;
    // this.xmlFileEditorService.getOriginalFileData(this.fileId).subscribe(
    //   (response) => {
    //     this.rawData = response;
    //     this.refreshTree();
    //     this.loading = false;
    //   },
    //   () => {
    //     this.loading = false;
    //   }
    // );
        this.rawData = JSON.parse(this.jsonData);
        this.refreshTree();
        this.loading = false;
  }

  onSave(): void {
    if (!this.rawData) {
      return;
    }
    this.saving = true;
    this.xmlFileEditorService.saveFileData(this.rawData, this.selectedCustomerId, this.msvEditFileName).subscribe(
      (data) => { 
        this.saving = false; 
        this.onSaveMsvFinished.emit(data);
      },
      () => { this.saving = false; }
    );
  }

  onSearchChange(): void {
    this.searchSubject.next();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.hideUnmatched = false;
    this.refreshTree();
  }

  onDelete(node: XmlTreeNode): void {
    if (!node || node.levelType === 'root' || !node.data || node.deleted) {
      return;
    }
    node.data['@uDeleted'] = '1';
    //node.data.deleted = '1';
    this.refreshTree();
  }

  // Resolves the value shown in a given detail column for a node, based on its level.
  // Column meaning changes per level (see task spec); nothing here reads/writes "data"
  // beyond the fields that already exist on it.
  getColumnValue(node: XmlTreeNode, field: string): string {
    const d = node.data || {};
    switch (node.levelType) {
      case 'root':
        return field === 'col1' ? '' : '';
      case 'fund':
        if (field === 'col1') { return this.asString(d['FundInstitutionName']); }
        if (field === 'col2') { return this.asString(d['SACH-HAFKADA-KUPA-H-P']); }
        if (field === 'col3') { return this.asString(d['MISPAR-ASMACHTA-LEAHAVARAT-KSAFIM']); }
        return '';
      case 'worker':
        if (field === 'col1') { return this.asString(d['SHEM-PRATI']); }
        if (field === 'col2') { return this.asString(d['SHEM-MISHPACHA']); }
        if (field === 'col3') { return this.asString(d['MISPAR-MEZAHE']); }        
        return '';
      case 'salary':
        return field === 'col1' ? this.asString(d['CHODESH-MASKORET']).substring(0, 7) : '';
      case 'allocation':
        if (field === 'col1') { return this.asString(d['SUG-HAFRASHA']); }
        if (field === 'col2') { return this.asString(d['SHIUR-HAFRASHA']); }
        if (field === 'col3') { return this.asString(d['SCHUM-HAFRASHA']); }
        return '';
      default:
        return '';
    }
  }

  // Rebuilds the whole displayed tree from rawData. Called after load, after every
  // delete, and (debounced) after every search keystroke. Rebuilding from the raw
  // object each time keeps deleted/matched/hidden state always in sync with rawData
  // instead of hand-patching a previously built tree.
  private refreshTree(): void {
    this.matchCount = 0;
    this.treeNodes = this.buildRootNode();
  }

  private buildRootNode(): XmlTreeNode[] {
    const root = this.rawData && this.rawData['YeshutGoremPoneLemislaka'];
    const funds = this.toArray(root ? root['PirteiHaavaratKsafim'] : null);

    const children = funds
      .map(f => this.buildFundNode(f))
      .filter((n): n is XmlTreeNode => n !== null);

    const rootNode: XmlTreeNode = {
      label: 'קובץ',
      levelType: 'root',
      data: {},
      expanded: true,
      children
    };
    return [rootNode];
  }

  private buildFundNode(fund: any): XmlTreeNode | null {
    const kupa = fund['PirteiKupa'] || {};
    const kupaName = fund['FundInstitutionName'] || {};
    const deleted = this.isDeleted(fund);
    let workerNodes = this.toArray(kupa['PirteiOved']).map(w => this.buildWorkerNode(w, deleted));

    if (this.isSearchActive() && this.hideUnmatched) {
      workerNodes = workerNodes.filter(n => n.matched);
      if (workerNodes.length === 0) {
        return null;
      }
    }

    return {
      label: 'הפקדה',
      //label2: kupaName,
      levelType: 'fund',
      data: fund,
      expanded: true,
      deleted,
      children: workerNodes
    };
  }

  private buildWorkerNode(worker: any, parentDeleted: boolean): XmlTreeNode {
    const matched = this.isWorkerMatch(worker);
    if (matched) {
      this.matchCount++;
    }

    const deleted = parentDeleted || this.isDeleted(worker);

    return {
      label: 'עובד',// + this.asString(worker['MISPAR-MEZAHE']),
      levelType: 'worker',
      data: worker,
      expanded: true,
      matched,
      deleted,
      children: this.toArray(worker['ChodeshMaskoretVestatusOved']).map(s => this.buildSalaryNode(s, deleted))
    };
  }

  private buildSalaryNode(salary: any, parentDeleted: boolean): XmlTreeNode {
    const deleted = parentDeleted || this.isDeleted(salary);

    return {
      label: 'חודש',  // + this.asString(salary['CHODESH-MASKORET']).substring(0, 7),
      levelType: 'salary',
      data: salary,
      expanded: true,
      deleted,
      children: this.toArray(salary['PizulHafrashotOvedBeKupa']).map(a => this.buildAllocationNode(a, deleted))
    };
  }

  private buildAllocationNode(allocation: any, parentDeleted: boolean): XmlTreeNode {
    return {
      label: this.asString(allocation['MISPAR-MEZAHE-RESHUMA']),  //'הפרשה ',
      label2: this.asString(allocation['MISPAR-MEZAHE-RESHUMA']),
      levelType: 'allocation',
      data: allocation,
      expanded: true,
      deleted: parentDeleted || this.isDeleted(allocation),
      leaf: true
    };
  }

  private isWorkerMatch(worker: any): boolean {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return false;
    }
    return SEARCHABLE_WORKER_FIELDS.some(field =>
      this.asString(worker[field]).toLowerCase().indexOf(term) !== -1
    );
  }

  private isSearchActive(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  // PirteiOved / ChodeshMaskoretVestatusOved / PizulHafrashotOvedBeKupa can each come
  // back as a single object OR an array depending on how many items there are -
  // normalize to an array without touching the original structure.
  private toArray(value: any): any[] {
    if (value === null || value === undefined) {
      return [];
    }
    return Array.isArray(value) ? value : [value];
  }

  private isDeleted(obj: any): boolean {
    return !!obj && obj['@uDeleted'] === '1';
  }

  // Guards against "@xsi:nil" placeholder objects (e.g. { "@xsi:nil": "true" })
  // showing up as [object Object] in the UI.
  private asString(value: any): string {
    return typeof value === 'string' ? value : '';
  }
}



// WEBPACK FOOTER //
// C:/ProjectsOnGit/USilence/USilence/Client/src/app/xml-file-editer/xml-file-editor.component.ts